import "./style.css";
import { useState, useEffect, useContext } from "react";
import { Col, Row, Form, Modal, message, Select } from "antd";
import { useMutation } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import Title from "../../../UI/Title/Title";
import { PrintContext, exchangeRateContext, returnedContext } from "../../MainFour";
import { getDeliveryApi } from "../../../../api/refDelivery";
import { DeliveryType } from "../../../References/Delivery/deliveryType";
import { setOpenProceed } from "../ProceedPayment/proceedpayment";
import { mainActions } from "../../../../store/main/mainSlice";
import { getIsPrintReceipt } from "../../../../utils/main-utils";

type payprops = {
  isPayOpen: boolean;
  setIsPayOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handlePay: (changeLBP: string, changeUSD: string) => void;
  currency: any;
  isPaid: boolean;
  setIsPaid: React.Dispatch<React.SetStateAction<boolean>>;
  setDeliveryCharge: React.Dispatch<React.SetStateAction<any>>;
  setDeliveryId: React.Dispatch<React.SetStateAction<any>>;
  deliveryCharge: number | null;
  isLoadingSaleMutation: boolean
};

const Delivery = ({
  isPayOpen,
  setIsPayOpen,
  handlePay,
  currency,
  setIsPaid,
  setDeliveryCharge,
  setDeliveryId,
  deliveryCharge,
  isLoadingSaleMutation
}: payprops) => {

  const [t] = useTranslation("common");
  const [form] = Form.useForm();

  // selectors
  const discount = useSelector((state: any) => state.main.discount);
  const finalPrice = useSelector((state: any) => state.main.finalPrice);
  const totalPrice = useSelector((state: any) => state.main.totalPrice);

  // context API
  const { exchangeRate } = useContext(exchangeRateContext);
  const { returnedLBP, returnedUSD, setReturnedLBP, setReturnedUSD } = useContext(returnedContext);
  const handlePrint = useContext(PrintContext);
  const setIsProceedOpen = useContext(setOpenProceed);

  // Redux
  const dispatch = useDispatch();

  const [deliveries, setDeliveries] = useState<DeliveryType[]>([])

  function numberWithCommas(x: number) {
    return x && x?.toFixed(2)?.replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ",");
  }
  function numberWithCommasLBP(x: number) {
    return x && x?.toFixed(0)?.replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ",");
  }

  const handlePayment = async () => {
    try {
      await handlePay(returnedLBP, returnedUSD)
      if (getIsPrintReceipt()) {
        await handlePrint();
      }
      setIsProceedOpen(false);
      setIsPaid(false);
      setDeliveryCharge(null)
      setDeliveryId(null)
      form.resetFields()
      dispatch(mainActions.clearInvoice());
    } catch (error: any) {
      message.error(error.response.data.message);
    }
  };

  const handleDeliveryChange = (e: any) => {
    setDeliveryId(e)
    const amount = deliveries.find((item: DeliveryType) => item.id === e)?.amount ?? 0
    setDeliveryCharge(amount)
  }

  const deliveriesDropdown = deliveries.map((item: DeliveryType) => {
    return (
      {
        label: item.location,
        value: item.id
      }
    )
  })

  const { mutateAsync: getDeliveriesMutation, isLoading: isGetDeliveriesLoading } =
    useMutation("deliveries", getDeliveryApi);

  useEffect(() => {
    getDeliveriesMutation({
      limit: 999999,
      q: "",
      isActive: true
    }).then((res) => {
      const responseData = res.data.data
      setDeliveries(responseData)
    })
  }, [])

  return (
    <>
      <Modal
        maskClosable={false}
        closable={false}
        width={500}
        centered
        open={isPayOpen}
        okButtonProps={{
          style: {
            height: "40px",
            background: deliveryCharge ? "#1677FF" : "gray",
            color: "#fff",
            width: "100px",
          },
          htmlType: "submit",
          form: "pay",
          disabled: !deliveryCharge,
          loading: isLoadingSaleMutation
        }}
        cancelButtonProps={{
          style: {
            color: "#1677FF",
            border: "1px solid #1677FF",
            height: "40px",
            width: "100px",
          },
        }}
        cancelText={t('cancel')}
        okText={`${getIsPrintReceipt() ? t("print") : t('save')}`}
        onCancel={() => {
          setIsPayOpen(false);
          setReturnedUSD("");
          setReturnedLBP("");
          form.resetFields()
          setDeliveryCharge(null)
          setDeliveryId(null)
        }}
        onOk={() => {
          handlePayment();
          document.body.classList.remove("body-scroll-lock");
        }}
      >
        <Form id="pay" layout="vertical" form={form}>
          <Col offset={1} span={23}>
            <Title title={`${t("delivery")}`} />
          </Col>
          <br />
          <Row style={{ display: 'flex', flexDirection: 'column' }}>
            <Col offset={1} style={{ marginInlineEnd: "4.166666666666666%" }}>
              <Form.Item name="delivery" label={`${t("location")}`}>
                <Select
                  size="large"
                  style={{ width: "100%" }}
                  placeholder={`${t("select-delivery-location")}`}
                  defaultActiveFirstOption
                  onChange={handleDeliveryChange}
                  loading={isGetDeliveriesLoading}
                  options={deliveriesDropdown}
                >
                </Select>
              </Form.Item>
            </Col>
            <br />
            <Col
              offset={1}
              style={{
                display: "flex",
                flexDirection: "column",
                alignContent: "center",
                gap: "20px",
                marginInlineEnd: "4.166666666666666%"
              }}
            >
              <Row style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Col className="titlestyle">
                  {" "}
                  {t("exchange-rate")}:
                </Col>
                <Col className="number-color">
                  {numberWithCommasLBP(exchangeRate || 1)}
                </Col>
              </Row>

              {/*-----------balance-------------- */}
              <Row style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Col className="titlestyle">
                  {t("balance-due")}:
                </Col>
                <Col className="number-color">
                  {numberWithCommas(totalPrice)} {currency?.abbreviation}
                </Col>
              </Row>

              {/*-----------discount-------------- */}
              <Row style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Col className="titlestyle">
                  {" "}
                  {t("discount")}:
                </Col>
                <Col className="number-color">
                  {discount?.amount?.toFixed(2)} {" "}
                  {discount.type === "#"
                    ? currency?.abbreviation
                    : discount?.type}
                </Col>
              </Row>
              <Row style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Col className="titlestyle">
                  {" "}
                  {t("delivery")} charge:
                </Col>
                <Col className="number-color">
                  {numberWithCommas(Number(deliveryCharge))}
                  {" USD"}
                </Col>
              </Row>

              {/*-----------total-------------- */}
              <Row style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Col className="titlestyle">
                  {" "}
                  {t("total")} USD:
                </Col>
                <Col className="number-color">
                  {numberWithCommas(Number(finalPrice))} {" "}
                  {currency?.abbreviation}
                </Col>
              </Row>
              <Row style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Col className="titlestyle">
                  {" "}
                  {t("total")} LBP:
                </Col>
                <Col className="number-color">
                  {numberWithCommasLBP(Number(finalPrice * exchangeRate))}
                  {" LBP"}
                </Col>
              </Row>
              <br />
            </Col>
          </Row>
        </Form>
      </Modal >
    </>
  );
};
export default Delivery;
